'use client'

import { createCart } from '@/lib/actions'
import type { CreateCartPayload } from '@/lib/actions/cart/create-cart'
import { useAppDispatch, useAppSelector, useModals } from '@/lib/hooks'
import {
  resetCheckout as resetCheckoutAction,
  setAmountToCollect as setAmountToCollectAction,
  setCurrentStep as setCurrentStepAction,
  setCustomReference as setCustomReferenceAction,
  setFirstDigits as setFirstDigitsAction,
  setFuturePaymentDate as setFuturePaymentDateAction,
  setLastDigits as setLastDigitsAction,
  setPaymentCard as setPaymentCardAction,
  setPaymentMethod as setPaymentMethodAction,
  setPaymentMethodType as setPaymentMethodTypeAction,
  setPaymentMethods as setPaymentMethodsAction,
  setPaymentsLoading as setPaymentsLoadingAction,
  setPayments as setPaymentsAction,
  setValues as setValuesAction,
} from '@/lib/redux'
import {
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  useQueryStates,
} from 'nuqs'
import { toast } from 'sonner'

export const useCheckout = () => {
  const dispatch = useAppDispatch()
  const checkout = useAppSelector((state) => state.checkout)

  const [queryParams, setQueryParams] = useQueryStates({
    isCheckout: parseAsBoolean,
    customerId: parseAsString,
    cartId: parseAsString,
    productFilterId: parseAsInteger,
    customerAccountRequired: parseAsBoolean,
    newSale: parseAsBoolean,
  })

  const { closeModal } = useModals()

  // Methods to dispatch various actions to Redux store
  const setCurrentStep = (step: CheckoutStep) =>
    dispatch(setCurrentStepAction(step))

  const setPayments = (payments: any) => dispatch(setPaymentsAction(payments))

  const setPaymentMethod = (method: any) =>
    dispatch(setPaymentMethodAction(method))

  const setPaymentMethodType = (method: PaymentMethod) =>
    dispatch(setPaymentMethodTypeAction(method))

  const setFirstDigits = (digits: any) => dispatch(setFirstDigitsAction(digits))
  const setLastDigits = (digits: any) => dispatch(setLastDigitsAction(digits))

  const setFuturePaymentDate = (date: string) =>
    dispatch(setFuturePaymentDateAction(date))

  const setCustomReference = (reference: string) =>
    dispatch(setCustomReferenceAction(reference))

  const setIsCheckout = async (isCheckout: boolean) =>
    await setQueryParams({ isCheckout })

  const setAmountToCollect = (amount: number) =>
    dispatch(setAmountToCollectAction(amount))

  const setPaymentCard = (card: PaymentMethodType | null) =>
    dispatch(setPaymentCardAction(card))

  const setPaymentsLoading = (loading: boolean) =>
    dispatch(setPaymentsLoadingAction(loading))

  const resetCheckout = () => dispatch(resetCheckoutAction())

  /*
   * Start a new sale
   */
  const startNewSale = async (payload?: CreateCartPayload) => {
    try {
      const cart = await createCart(payload)
      return cart
    } catch (error: any) {
      return null
    }
  }

  /*
   * Cancel cart
   */
  const cancelCart = (cartId?: string) => {
    const cartWorker = new Worker(
      new URL('@/lib/workers/cart.worker', import.meta.url),
    )
    cartWorker.postMessage({
      type: 'CANCEL_CART',
      payload: { cartId },
    })

    cartWorker.onmessage = (event) => {
      const { type, error, data } = event.data

      if (type === 'ERROR') {
        toast.error(error)
        return
      }

      resetCheckout()
      cartWorker.terminate()
    }
  }

  /*
   * Void order
   */
  const voidOrder = (payload?: any) => {
    const orderWorker = new Worker(
      new URL('@/lib/workers/cart.worker', import.meta.url),
    )
    orderWorker.postMessage({
      type: 'VOID_ORDER',
      payload: payload,
    })

    orderWorker.onmessage = (event) => {
      const { type, error, data } = event.data

      if (type === 'ERROR') {
        toast.error(error)
        orderWorker.terminate()
        return
      }

      orderWorker.terminate()
    }

    orderWorker.onerror = (error) => {
      console.error('Worker error:', error)
      toast.error('An error occurred while voiding the order')
      orderWorker.terminate()
    }
  }

  /*
   * Delete Payment
   */
  const deletePayment = (paymentId?: string) => {
    const paymentWorker = new Worker(
      new URL('@/lib/workers/payment.worker', import.meta.url),
    )
    paymentWorker.postMessage({
      type: 'DELETE_PAYMENT',
      payload: { paymentId },
    })

    paymentWorker.onmessage = (event) => {
      const { type, error, data } = event.data

      if (type === 'ERROR') {
        toast.error(error)
        return
      }

      getPayments({ cartId: queryParams.cartId })

      toast.success('Payment deleted successfully')
      paymentWorker.terminate()
    }
  }

  /*
   * Set payment methods
   */
  const setPaymentMethods = async () => {
    const paymentWorker = new Worker(
      new URL('@/lib/workers/payment.worker', import.meta.url),
    )
    paymentWorker.postMessage({ type: 'GET_PAYMENT_METHODS' })
    paymentWorker.onmessage = (event) => {
      const { type, error, data } = event.data
      if (type === 'ERROR') {
        toast.error(error)
        return
      }
      // Only set default payment method if none is currently selected
      if (!checkout.payment_method) {
        setPaymentMethod(data?.cash?.[0])
      }
      dispatch(setPaymentMethodsAction(data))
      paymentWorker.terminate()
    }
  }

  /*
   * Get payments
   */
  const getPayments = (_payload: any) => {
    const { callbackFn, ...payload } = _payload
    setPaymentsLoading(true)

    const paymentWorker = new Worker(
      new URL('@/lib/workers/payment.worker', import.meta.url),
    )

    paymentWorker.postMessage({ type: 'GET_PAYMENTS', payload })
    paymentWorker.onmessage = (event) => {
      const { type, error, data } = event.data

      switch (type) {
        case 'ERROR':
          toast.error(error)
          callbackFn?.(data)
          paymentWorker.terminate()
          break
        case 'GET_PAYMENTS_SUCCESS':
          // biome-ignore lint/correctness/noSwitchDeclarations: <explanation>
          const { payments, total_paid, balance_to_paid, over_paid } = data

          dispatch(
            setValuesAction({
              payments,
              total_paid,
              balance_to_paid,
              over_paid,
            }),
          )

          setPaymentsLoading(false)
          if (payments.length === 0) {
            closeModal('VIEW_CART_PAYMENTS')
          }
          callbackFn?.(data)
          paymentWorker.terminate()
          break
        default:
          console.error(`Unexpected event type: ${type}`)
          callbackFn?.(data)
          paymentWorker.terminate()
      }
    }
  }

  const fully_paid = checkout.balance_to_paid === 0 && checkout.total_paid > 0

  return {
    ...checkout,
    cancelCart,
    deletePayment,
    fully_paid,
    getPayments,
    queryParams,
    resetCheckout,
    setAmountToCollect,
    setCurrentStep,
    setFirstDigits,
    setIsCheckout,
    setLastDigits,
    setPaymentCard,
    setPaymentMethod,
    setPaymentMethodType,
    setPaymentMethods,
    setPayments,
    setPaymentsLoading,
    setQueryParams,
    setFuturePaymentDate,
    setCustomReference,
    startNewSale,
    futurePaymentDate: checkout.future_payment_date,
    customReference: checkout.custom_reference,
    voidOrder,
  }
}
